import {captureException} from '@sentry/browser';
import {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {getContextIdsFromActiveWorkspace} from 'llm/chat-library/ChatDAO';
import {
  invokeVoiceTool,
  persistVoiceTranscript,
  PersistVoiceTranscriptRequest,
  startWebRTCRealtimeVoiceSession,
} from 'llm/data-capture/DataCaptureDAO';
import {
  isAssistantOutputItemAddedEvent,
  isAssistantTranscriptDoneEvent,
  isConversationItemAcknowledgementEvent,
  isConversationItemAddedEvent,
  isConversationItemCreatedEvent,
  isFunctionCallDoneEvent,
  isInputAudioBufferClearedEvent,
  isInputAudioBufferSpeechStartedEvent,
  isInputAudioBufferSpeechStoppedEvent,
  isOutputAudioBufferClearedEvent,
  isOutputAudioBufferStartedEvent,
  isOutputAudioBufferStoppedEvent,
  isResponseCreatedEvent,
  isResponseDoneEvent,
  isRealtimeErrorEvent,
  isSessionCreatedEvent,
  isSessionUpdatedEvent,
  isUserTranscriptionCompletedEvent,
  isUserTranscriptionFailedEvent,
  createConversationHistoryItemEvent,
  createSessionUpdateEvent,
  createToolResponseEvent,
  createTriggerInferenceEvent,
  RealtimeSessionUpdate,
} from 'llm/realtime-voice/OpenAIRealtimeEvents';
import {
  beginPendingVoiceToolInvocation,
  settlePendingVoiceToolInvocation,
} from 'llm/realtime-voice/PendingVoiceToolInvocations';
import {
  clearPendingVoiceTranscripts,
  getPendingVoiceTranscripts,
  markPendingVoiceTranscriptPersisted,
  removePendingVoiceTranscript,
  upsertPendingVoiceTranscript,
} from 'llm/realtime-voice/PendingVoiceTranscripts';
import notifications from 'notifications';
import APIUtil from 'util/APIUtil';
import {isServerRequestError} from 'util/Net';

import WebRTCSession from './WebRTCSession';

// Persist a terminal fallback rather than silently losing a stalled user transcription.
const USER_TRANSCRIPTION_TIMEOUT_MS = 15_000;
const WEBRTC_DISCONNECT_GRACE_MS = 5_000;
// Shown to the user via `notifications.error` when a session refresh fails; Voice is stopped
// immediately after, so this explains why the session ended.
const SESSION_REFRESH_FAILURE_MESSAGE =
  'Voice stopped because its available actions could not be refreshed. Restart Voice to continue.';
// Sent to the Realtime model as the `function_call_output` for the tool call that triggered the
// failed refresh. The underlying tool action already succeeded server-side, so this settles the
// pending call (rather than leaving the model waiting on it forever) while the session is torn down.
const SESSION_REFRESH_FAILURE_TOOL_OUTPUT =
  'The action completed, but Voice could not refresh its available actions. Restart Voice before continuing.';

interface VoiceEntryReference {
  actionId: string;
  documentId: string;
}

export enum RealtimeVoiceSessionState {
  NOT_CONNECTED = 'NOT_CONNECTED',
  CONNECTING = 'CONNECTING',
  CONNECTED = 'CONNECTED',
}

export interface RealtimeVoiceSessionControls {
  sessionState: RealtimeVoiceSessionState;
  isUserSpeaking: boolean;
  isAssistantWorking: boolean;
  isAssistantSpeaking: boolean;
  microphoneLevel: MicrophoneLevelSource;
  startVoiceSession: () => Promise<void>;
  stopVoiceSession: () => Promise<void>;
}

export interface UseRealtimeVoiceSessionOptions {
  /**
   * Called after Voice successfully invokes CreateDraftDocument.
   * This is the Voice create action, not a later application-state snapshot.
   */
  onDraftDocumentCreated?: () => void;
}

export interface MicrophoneLevelSource {
  getSnapshot: () => number;
  subscribe: (listener: () => void) => () => void;
}

interface VoiceSessionAttempt {
  session: WebRTCSession | null;
  activeWorkspaceContextId?: string;
  openaiRealtimeSessionId?: string;
}

interface InterruptedUserTranscript {
  itemId: string;
  previousItemId: string | null;
}

function createMicrophoneLevelSource(): MicrophoneLevelSource & {set: (level: number) => void} {
  let level = 0;
  const listeners = new Set<() => void>();
  return {
    getSnapshot: () => level,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    set: (nextLevel) => {
      if (level === nextLevel) {
        return;
      }
      level = nextLevel;
      listeners.forEach((listener) => {
        listener();
      });
    },
  };
}

interface ExecuteRealtimeVoiceToolCallArgs {
  chatSessionId: string;
  name: string;
  callId: string;
  // JSON-encoded string of the function call's arguments, exactly as emitted by the Realtime
  // model (i.e. `conversation.item.done`'s `item.arguments`). Forwarded as-is to `invokeVoiceTool`,
  // which is responsible for parsing it server-side.
  args: string;
  activeWorkspaceContextId?: string;
  webRTCSession: WebRTCSession;
  isCurrentSession: () => boolean;
  onDraftDocumentCreated: () => void;
  onDisplayReference: (actionId: string, documentId: string) => void;
  stopVoiceSession: () => Promise<void>;
}

/**
 * Runs `executeToolCall` only after any previously enqueued call on this ref has settled, so
 * that tool calls from the same session are executed one at a time and in the order Realtime
 * emitted them (matching Unified's root-thread write lock).
 *
 * A prior call's rejection is intentionally swallowed here (`.catch(() => undefined)`) only to
 * unblock the queue for the *next* call - it does not suppress error reporting for that prior
 * call, since its own promise (returned to its own caller) still rejects and is handled/reported
 * there.
 */
async function enqueueSerializedToolCall(
  queueRef: {current: Promise<void> | null},
  executeToolCall: () => Promise<void>
): Promise<void> {
  const previousToolCall = queueRef.current;
  const currentToolCall =
    previousToolCall == null
      ? executeToolCall()
      : previousToolCall.catch(() => undefined).then(executeToolCall);
  queueRef.current = currentToolCall;
  try {
    await currentToolCall;
  } finally {
    if (queueRef.current === currentToolCall) {
      queueRef.current = null;
    }
  }
}

/**
 * Sends a `session.update` with the server-authorized instructions/tools for the new target
 * state, and waits for the provider to acknowledge it before returning.
 *
 * Returns `'applied'` on success, `'stale'` if the session was stopped/restarted while waiting
 * (in which case the caller should do nothing further), or `'failed'` if sending the update
 * threw or the provider rejected it. Failure is reported via `captureException`; the caller is
 * responsible for notifying the user and stopping the session.
 */
async function applyRealtimeSessionUpdate(
  webRTCSession: WebRTCSession,
  sessionUpdate: RealtimeSessionUpdate,
  callId: string,
  isCurrentSession: () => boolean
): Promise<'applied' | 'stale' | 'failed'> {
  const sessionUpdateEventId = `voice_session_update_${callId}`;
  const sessionUpdateAbortController = new AbortController();
  // Realtime echoes this client event ID only on errors. Successful session.updated
  // events are uncorrelated, so the serialized tool-call queue must keep one update
  // acknowledgement outstanding per session.
  const sessionUpdateAcknowledgementPromise = webRTCSession.waitForDataEvent(
    isSessionUpdatedEvent,
    (event) => {
      if (isRealtimeErrorEvent(event) && event.error.event_id === sessionUpdateEventId) {
        return new Error(event.error.message);
      }
      return null;
    },
    sessionUpdateAbortController.signal
  );
  try {
    webRTCSession.sendDataEvent(createSessionUpdateEvent(sessionUpdate, sessionUpdateEventId));
  } catch (error) {
    sessionUpdateAbortController.abort(error);
    await sessionUpdateAcknowledgementPromise.catch(() => {});
    // Treat a failure to even send the update the same as a rejected/timed-out update, so the
    // caller's existing notify-and-stop path runs instead of leaving Voice hung on stale
    // instructions/tools with an unresolved tool call.
    captureException(error, {tags: {'voice-session-update-phase': 'refresh'}});
    if (!(error instanceof Error)) {
      throw error;
    }
    return 'failed';
  }
  const sessionUpdateError = await sessionUpdateAcknowledgementPromise.then(
    () => null,
    (error: unknown) => error
  );
  if (sessionUpdateError != null) {
    if (!isCurrentSession()) {
      return 'stale';
    }
    captureException(sessionUpdateError, {tags: {'voice-session-update-phase': 'refresh'}});
    try {
      webRTCSession.sendDataEvent(createToolResponseEvent(callId, SESSION_REFRESH_FAILURE_TOOL_OUTPUT));
    } catch (sendError) {
      captureException(sendError, {
        tags: {'voice-session-update-phase': 'failure-output'},
      });
      if (!(sendError instanceof Error)) {
        throw sendError;
      }
    }
    return 'failed';
  }
  if (!isCurrentSession()) {
    return 'stale';
  }
  return 'applied';
}

async function executeRealtimeVoiceToolCall({
  chatSessionId,
  name,
  callId,
  args,
  activeWorkspaceContextId,
  webRTCSession,
  isCurrentSession,
  onDraftDocumentCreated,
  onDisplayReference,
  stopVoiceSession,
}: ExecuteRealtimeVoiceToolCallArgs): Promise<void> {
  if (!isCurrentSession()) {
    return;
  }
  beginPendingVoiceToolInvocation(chatSessionId, callId);
  try {
    let invocationResult: Awaited<ReturnType<typeof invokeVoiceTool>>;
    try {
      invocationResult = await invokeVoiceTool(chatSessionId, {name, callId, args}, activeWorkspaceContextId);
    } catch (error: unknown) {
      if (!isServerRequestError(error)) {
        throw error;
      }
      invocationResult = {
        output:
          'The tool request outcome is uncertain. Do not retry a write automatically; verify the current state or tell the user that the result could not be confirmed.',
        sessionUpdate: undefined,
        draftDocumentCreated: false,
      };
    }
    const {output, sessionUpdate, draftDocumentCreated, displayReferenceId} = invocationResult;
    if (!isCurrentSession()) {
      return;
    }
    if (displayReferenceId != null) {
      onDisplayReference(callId, displayReferenceId);
    }
    if (draftDocumentCreated) {
      onDraftDocumentCreated();
    }
    if (sessionUpdate != null) {
      const refreshResult = await applyRealtimeSessionUpdate(
        webRTCSession,
        sessionUpdate,
        callId,
        isCurrentSession
      );
      if (refreshResult === 'stale') {
        return;
      }
      if (refreshResult === 'failed') {
        notifications.error(SESSION_REFRESH_FAILURE_MESSAGE, {
          id: 'realtime-voice-session-refresh-error',
        });
        await stopVoiceSession();
        return;
      }
    }
    webRTCSession.sendDataEvent(createToolResponseEvent(callId, output));
    webRTCSession.sendDataEvent(createTriggerInferenceEvent());
  } finally {
    settlePendingVoiceToolInvocation(chatSessionId, callId);
  }
}

/**
 * Hook to manage a realtime voice session.
 *
 * Currently this is implemented as a WebRTC connection to OpenAI's Realtime API.
 */
export default function useRealtimeVoiceSession(
  chatSessionId: string | null,
  agentType: AgentType = AgentType.ASSISTANT,
  options: UseRealtimeVoiceSessionOptions = {}
): RealtimeVoiceSessionControls {
  const onDraftDocumentCreatedRef = useRef(options.onDraftDocumentCreated);
  onDraftDocumentCreatedRef.current = options.onDraftDocumentCreated;
  const [sessionState, setSessionState] = useState<RealtimeVoiceSessionState>(
    RealtimeVoiceSessionState.NOT_CONNECTED
  );
  const webRTCSessionRef = useRef<WebRTCSession | null>(null);
  const activeSessionAttemptRef = useRef<VoiceSessionAttempt | null>(null);
  const sessionStartTimeMsRef = useRef<number | null>(null);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [isAssistantWorking, setIsAssistantWorking] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const microphoneLevelRef = useRef(createMicrophoneLevelSource());
  const speakingUserItemIdsRef = useRef<Set<string>>(new Set());
  const playingAssistantResponseIdsRef = useRef<Set<string>>(new Set());
  // Tracks whether the component is still mounted. Used to detect unmount during async operations
  // where webRTCSessionRef.current may not yet be set (e.g., during WebRTCSession.create()).
  const isMountedRef = useRef(true);
  // Capture ordering before asynchronous user transcription completes.
  const previousItemIdByItemIdRef = useRef<Map<string, string | null>>(new Map());
  // This longer-lived deduplication guard is distinct from the startup acknowledgement barrier
  // below: it keeps replay echoes from being mistaken for new live turns after they are accepted.
  const replayedHistoryItemIdsRef = useRef<Set<string>>(new Set());
  // Pending never-drop timeouts for user items awaiting transcription, keyed by item id.
  const pendingTranscriptionTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  // User placeholders created by speech_started but not yet committed as conversation items.
  const earlyUserItemIdsRef = useRef<Set<string>>(new Set());
  // Assistant output items are announced before their transcript and are correlated to response
  // failures by response ID.
  const assistantItemIdByResponseIdRef = useRef<Map<string, string>>(new Map());
  const disconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const claimedInterruptedTranscriptItemIdsRef = useRef<Set<string>>(new Set());
  const toolCallQueueRef = useRef<Promise<void> | null>(null);
  // Entry references queued by tool calls for the next assistant transcript persist.
  // transcriptStateGenerationRef is bumped whenever this queue is reset (session stop/cleanup),
  // so an in-flight persistTranscript can tell whether it's still safe to restore a failed
  // reference back onto the queue.
  const entryReferencesRef = useRef<Array<VoiceEntryReference>>([]);
  const transcriptStateGenerationRef = useRef(0);

  const clearPendingTranscriptionTimeout = useCallback((itemId: string) => {
    const timeoutId = pendingTranscriptionTimeoutsRef.current.get(itemId);
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      pendingTranscriptionTimeoutsRef.current.delete(itemId);
    }
  }, []);

  // The hook instance outlives any single session, so without this a new session would inherit
  // stale transcript/reference state left over from the one that just stopped. Bumping the
  // generation here is what tells an in-flight persistTranscript's catch block not to restore
  // into this now-reset queue.
  const clearPendingTranscriptState = useCallback(() => {
    transcriptStateGenerationRef.current += 1;
    pendingTranscriptionTimeoutsRef.current.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    pendingTranscriptionTimeoutsRef.current.clear();
    earlyUserItemIdsRef.current.clear();
    assistantItemIdByResponseIdRef.current.clear();
    replayedHistoryItemIdsRef.current.clear();
    entryReferencesRef.current = [];
    if (chatSessionId != null) {
      clearPendingVoiceTranscripts(chatSessionId);
    }
  }, [chatSessionId]);

  // actionId-keyed so a duplicate/retried tool response can't queue the same reference twice.
  const queueEntryReference = useCallback((actionId: string, documentId: string): void => {
    if (entryReferencesRef.current.some((reference) => reference.actionId === actionId)) {
      return;
    }
    entryReferencesRef.current.push({actionId, documentId});
  }, []);

  const clearDisconnectTimeout = useCallback(() => {
    if (disconnectTimeoutRef.current != null) {
      clearTimeout(disconnectTimeoutRef.current);
      disconnectTimeoutRef.current = null;
    }
  }, []);

  const claimInterruptedUserTranscripts = useCallback((): Array<InterruptedUserTranscript> => {
    if (chatSessionId == null) {
      return [];
    }
    return getPendingVoiceTranscripts(chatSessionId).flatMap(
      ({itemId, persistedEventId, previousItemId, role}) => {
        if (
          persistedEventId != null ||
          role !== 'user' ||
          claimedInterruptedTranscriptItemIdsRef.current.has(itemId)
        ) {
          return [];
        }
        claimedInterruptedTranscriptItemIdsRef.current.add(itemId);
        return [{itemId, previousItemId}];
      }
    );
  }, [chatSessionId]);

  const persistInterruptedUserTranscripts = useCallback(
    async (
      interruptedUserTranscripts: Array<InterruptedUserTranscript>,
      openaiRealtimeSessionId?: string
    ): Promise<void> => {
      if (chatSessionId == null) {
        return;
      }
      await Promise.all(
        interruptedUserTranscripts.map(({itemId, previousItemId}) =>
          persistVoiceTranscript(chatSessionId, {
            role: 'user',
            text: '',
            itemId,
            previousItemId,
            failed: true,
            ...(openaiRealtimeSessionId == null ? {} : {openaiRealtimeSessionId}),
          }).catch((error) => {
            captureException(error, {tags: {'voice-transcript-persist-phase': 'interrupted'}});
          })
        )
      );
    },
    [chatSessionId]
  );

  // Disconnect, release the microphone, and cancel pending transcript fallbacks on unmount.
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      const openaiRealtimeSessionId = activeSessionAttemptRef.current?.openaiRealtimeSessionId;
      activeSessionAttemptRef.current = null;
      const webRTCSession = webRTCSessionRef.current;
      webRTCSessionRef.current = null;
      const closePromise =
        webRTCSession == null
          ? Promise.resolve()
          : Promise.resolve(webRTCSession.close()).catch(captureException);
      const interruptedUserTranscripts = claimInterruptedUserTranscripts();
      clearDisconnectTimeout();
      clearPendingTranscriptState();
      closePromise
        .then(() => persistInterruptedUserTranscripts(interruptedUserTranscripts, openaiRealtimeSessionId))
        .catch(captureException);
    };
  }, [
    claimInterruptedUserTranscripts,
    clearDisconnectTimeout,
    clearPendingTranscriptState,
    persistInterruptedUserTranscripts,
  ]);

  const persistTranscript = useCallback(
    async (
      role: 'user' | 'assistant',
      text: string,
      itemId: string,
      failed = false,
      openaiRealtimeSessionId?: string
    ) => {
      assert(chatSessionId != null, 'Chat session ID should be set during voice session');
      // Snapshotted before the request so the catch block below can tell whether the queue has
      // since been reset (new session) and therefore isn't safe to write back into.
      const transcriptStateGeneration = transcriptStateGenerationRef.current;
      const request: PersistVoiceTranscriptRequest = {role, text, itemId};
      if (openaiRealtimeSessionId != null) {
        request.openaiRealtimeSessionId = openaiRealtimeSessionId;
      }
      const displayReferences = role === 'assistant' ? entryReferencesRef.current : [];
      if (displayReferences.length > 0) {
        // Cleared optimistically so a reference isn't attached to two transcript persists; put
        // back in the catch block below if this persist fails.
        entryReferencesRef.current = [];
        request.text = [
          text,
          ...displayReferences.map(({documentId}) => `<bnch_id>${documentId}</bnch_id>`),
        ].join('\n\n');
      }
      const previousItemId = previousItemIdByItemIdRef.current.get(itemId);
      if (previousItemId !== undefined) {
        request.previousItemId = previousItemId;
      }
      if (failed) {
        request.failed = true;
      }
      try {
        const {eventId} = await persistVoiceTranscript(chatSessionId, request);
        markPendingVoiceTranscriptPersisted(chatSessionId, itemId, eventId);
      } catch (error) {
        // Only restore into the current queue if the generation still matches - otherwise the
        // session was reset while this request was in flight, and requeuing would leak this
        // reference into whatever new session started in the meantime.
        if (transcriptStateGeneration === transcriptStateGenerationRef.current) {
          const queuedActionIds = new Set(entryReferencesRef.current.map(({actionId}) => actionId));
          entryReferencesRef.current = [
            ...displayReferences.filter(({actionId}) => !queuedActionIds.has(actionId)),
            ...entryReferencesRef.current,
          ];
        }
        throw error;
      }
    },
    [chatSessionId]
  );

  const stopVoiceSession = useCallback(async () => {
    const sessionStartTimeMs = sessionStartTimeMsRef.current;
    sessionStartTimeMsRef.current = null;
    const openaiRealtimeSessionId = activeSessionAttemptRef.current?.openaiRealtimeSessionId;
    activeSessionAttemptRef.current = null;
    const webRTCSession = webRTCSessionRef.current;
    webRTCSessionRef.current = null;
    const interruptedUserTranscripts = claimInterruptedUserTranscripts();

    const closePromise =
      webRTCSession == null
        ? null
        : Promise.resolve(webRTCSession.close()).catch((error) => {
            captureException(error);
          });
    clearDisconnectTimeout();
    clearPendingTranscriptState();
    speakingUserItemIdsRef.current.clear();
    playingAssistantResponseIdsRef.current.clear();
    setIsUserSpeaking(false);
    setIsAssistantWorking(false);
    setIsAssistantSpeaking(false);
    microphoneLevelRef.current.set(0);
    setSessionState(RealtimeVoiceSessionState.NOT_CONNECTED);
    if (closePromise != null) {
      await closePromise;
    }

    await persistInterruptedUserTranscripts(interruptedUserTranscripts, openaiRealtimeSessionId);

    if (chatSessionId != null && sessionStartTimeMs != null) {
      const durationMs = Date.now() - sessionStartTimeMs;
      await Promise.resolve()
        .then(() => {
          trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.ENDED_COMPOSE_REALTIME_VOICE_SESSION, {
            chatSessionId,
            durationMs,
          });
        })
        .catch(captureException);
    }
  }, [
    chatSessionId,
    claimInterruptedUserTranscripts,
    clearDisconnectTimeout,
    clearPendingTranscriptState,
    persistInterruptedUserTranscripts,
  ]);

  const handleDataEvent = useCallback(
    async (data: unknown, attempt: VoiceSessionAttempt) => {
      assert(chatSessionId != null, 'Chat session ID should be set during voice session');
      const webRTCSession = webRTCSessionRef.current;
      if (webRTCSession == null) {
        return;
      }
      if (isSessionCreatedEvent(data)) {
        attempt.openaiRealtimeSessionId = data.session.id;
        return;
      }
      if (isFunctionCallDoneEvent(data)) {
        const {name, call_id: callId, arguments: args} = data.item;
        // Unified serializes writes with the chat's root-thread lock. Realtime may emit multiple
        // completed function calls together, so preserve their order instead of racing the lock.
        await enqueueSerializedToolCall(toolCallQueueRef, () =>
          executeRealtimeVoiceToolCall({
            chatSessionId,
            name,
            callId,
            args,
            activeWorkspaceContextId: attempt.activeWorkspaceContextId,
            webRTCSession,
            isCurrentSession: () => webRTCSessionRef.current === webRTCSession,
            onDraftDocumentCreated: () => onDraftDocumentCreatedRef.current?.(),
            onDisplayReference: queueEntryReference,
            stopVoiceSession,
          })
        );
        return;
      }

      if (isInputAudioBufferSpeechStartedEvent(data)) {
        speakingUserItemIdsRef.current.add(data.item_id);
        setIsUserSpeaking(true);
        earlyUserItemIdsRef.current.add(data.item_id);
        upsertPendingVoiceTranscript(chatSessionId, {
          itemId: data.item_id,
          previousItemId: null,
          role: 'user',
        });
        return;
      }
      if (isInputAudioBufferSpeechStoppedEvent(data)) {
        speakingUserItemIdsRef.current.delete(data.item_id);
        setIsUserSpeaking(speakingUserItemIdsRef.current.size > 0);
        return;
      }
      if (isInputAudioBufferClearedEvent(data)) {
        speakingUserItemIdsRef.current.clear();
        setIsUserSpeaking(false);
        earlyUserItemIdsRef.current.forEach((itemId) => {
          removePendingVoiceTranscript(chatSessionId, itemId);
        });
        earlyUserItemIdsRef.current.clear();
        return;
      }
      if (isOutputAudioBufferStartedEvent(data)) {
        playingAssistantResponseIdsRef.current.add(data.response_id);
        setIsAssistantWorking(false);
        setIsAssistantSpeaking(true);
        return;
      }
      if (isOutputAudioBufferStoppedEvent(data) || isOutputAudioBufferClearedEvent(data)) {
        playingAssistantResponseIdsRef.current.delete(data.response_id);
        setIsAssistantSpeaking(playingAssistantResponseIdsRef.current.size > 0);
        return;
      }
      if (isAssistantOutputItemAddedEvent(data)) {
        assistantItemIdByResponseIdRef.current.set(data.response_id, data.item.id);
        upsertPendingVoiceTranscript(chatSessionId, {
          itemId: data.item.id,
          previousItemId: null,
          role: 'assistant',
        });
        return;
      }
      if (isResponseCreatedEvent(data)) {
        setIsAssistantWorking(true);
        return;
      }
      if (isResponseDoneEvent(data)) {
        setIsAssistantWorking(false);
        const assistantItemId = assistantItemIdByResponseIdRef.current.get(data.response.id);
        assistantItemIdByResponseIdRef.current.delete(data.response.id);
        if (assistantItemId != null && data.response.status !== 'completed') {
          removePendingVoiceTranscript(chatSessionId, assistantItemId);
        }
        return;
      }
      if (isConversationItemCreatedEvent(data) || isConversationItemAddedEvent(data)) {
        const itemId = data.item.id;
        if (replayedHistoryItemIdsRef.current.delete(itemId)) {
          return;
        }
        const previousItemId = data.previous_item_id ?? null;
        previousItemIdByItemIdRef.current.set(itemId, previousItemId);
        if (data.item.role === 'user' || data.item.role === 'assistant') {
          upsertPendingVoiceTranscript(chatSessionId, {
            itemId,
            previousItemId,
            role: data.item.role,
          });
        }
        // Only user items go through asynchronous STT and can fail or time out.
        if (data.item.role === 'user') {
          earlyUserItemIdsRef.current.delete(itemId);
          clearPendingTranscriptionTimeout(itemId);
          const timeoutId = setTimeout(() => {
            pendingTranscriptionTimeoutsRef.current.delete(itemId);
            persistTranscript('user', '', itemId, true, attempt.openaiRealtimeSessionId).catch((error) => {
              captureException(error, {tags: {'voice-transcript-persist-phase': 'timeout'}});
            });
          }, USER_TRANSCRIPTION_TIMEOUT_MS);
          pendingTranscriptionTimeoutsRef.current.set(itemId, timeoutId);
        }
        return;
      }
      if (isAssistantTranscriptDoneEvent(data)) {
        try {
          await persistTranscript(
            'assistant',
            data.transcript,
            data.item_id,
            false,
            attempt.openaiRealtimeSessionId
          );
        } catch (error) {
          removePendingVoiceTranscript(chatSessionId, data.item_id);
          throw error;
        }
        return;
      }
      if (isUserTranscriptionCompletedEvent(data)) {
        await persistTranscript(
          'user',
          data.transcript,
          data.item_id,
          false,
          attempt.openaiRealtimeSessionId
        );
        clearPendingTranscriptionTimeout(data.item_id);
        return;
      }
      if (isUserTranscriptionFailedEvent(data)) {
        await persistTranscript('user', '', data.item_id, true, attempt.openaiRealtimeSessionId);
        clearPendingTranscriptionTimeout(data.item_id);
      }
    },
    [
      chatSessionId,
      clearPendingTranscriptionTimeout,
      persistTranscript,
      queueEntryReference,
      stopVoiceSession,
    ]
  );

  const handleWebRTCConnectionStateChange = useCallback(
    (connectionState: RTCPeerConnectionState) => {
      if (connectionState === 'connected') {
        clearDisconnectTimeout();
        return;
      }
      if (connectionState === 'disconnected') {
        if (disconnectTimeoutRef.current == null) {
          disconnectTimeoutRef.current = setTimeout(() => {
            disconnectTimeoutRef.current = null;
            stopVoiceSession().catch(captureException);
          }, WEBRTC_DISCONNECT_GRACE_MS);
        }
        return;
      }
      if (
        (connectionState === 'failed' || connectionState === 'closed') &&
        webRTCSessionRef.current != null
      ) {
        stopVoiceSession().catch(captureException);
      }
    },
    [clearDisconnectTimeout, stopVoiceSession]
  );

  const startVoiceSession = useCallback(async () => {
    assert(chatSessionId != null, 'Unexpectedly started voice session without a chat session ID.');
    if (activeSessionAttemptRef.current != null) {
      return;
    }
    const attempt: VoiceSessionAttempt = {session: null};
    activeSessionAttemptRef.current = attempt;
    setSessionState(RealtimeVoiceSessionState.CONNECTING);

    try {
      // Create into a local variable first, then check if we should abort before assigning.
      // This handles unmount during WebRTCSession.create() (e.g., during mic permission prompt).
      const webRTCSession = await WebRTCSession.create(
        (data) => {
          if (activeSessionAttemptRef.current !== attempt || attempt.session == null) {
            return;
          }
          return handleDataEvent(data, attempt);
        },
        (connectionState) => {
          if (activeSessionAttemptRef.current === attempt && attempt.session != null) {
            handleWebRTCConnectionStateChange(connectionState);
          }
        },
        (level) => {
          if (isMountedRef.current && activeSessionAttemptRef.current === attempt) {
            microphoneLevelRef.current.set(level);
          }
        }
      );
      if (!isMountedRef.current || activeSessionAttemptRef.current !== attempt) {
        await webRTCSession.close();
        return;
      }
      attempt.session = webRTCSession;
      webRTCSessionRef.current = webRTCSession;

      const initialSDP = await webRTCSession.createSDPOffer();
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      const [activeWorkspaceId] = getContextIdsFromActiveWorkspace(agentType);
      const activeWorkspaceContextId =
        activeWorkspaceId != null && APIUtil.isEntry(activeWorkspaceId) ? activeWorkspaceId : undefined;
      attempt.activeWorkspaceContextId = activeWorkspaceContextId;
      const {sdpAnswer, conversationHistory} = await startWebRTCRealtimeVoiceSession(
        chatSessionId,
        initialSDP,
        activeWorkspaceContextId
      );
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      await webRTCSession.startSessionWithSDPAnswer(sdpAnswer);
      // Verify we're still on the same session - abort if unmounted or session changed.
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }

      await webRTCSession.waitForDataChannelOpen();
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }
      const historyReplayEvents =
        conversationHistory?.map((item) => createConversationHistoryItemEvent(item)) ?? [];
      conversationHistory?.forEach((item) => {
        replayedHistoryItemIdsRef.current.add(item.id);
      });
      if (historyReplayEvents.length > 0) {
        // This startup-only set is an acknowledgement barrier. The ref above has a longer lifetime
        // and prevents acknowledged replay echoes from being treated as new live transcript items.
        const pendingItemIds = new Set(historyReplayEvents.map(({item}) => item.id));
        const replayWaitAbortController = new AbortController();
        const replayAcknowledgementPromise = webRTCSession.waitForDataEvent(
          (data) => {
            if (!isConversationItemAcknowledgementEvent(data)) {
              return false;
            }
            pendingItemIds.delete(data.item.id);
            return pendingItemIds.size === 0;
          },
          (data) => {
            if (!isRealtimeErrorEvent(data)) {
              return null;
            }
            return new Error(data.error.message);
          },
          replayWaitAbortController.signal
        );
        try {
          historyReplayEvents.forEach((event) => {
            webRTCSession.sendDataEvent(event);
          });
        } catch (error) {
          replayWaitAbortController.abort(error);
          await replayAcknowledgementPromise.catch(() => {});
          throw error;
        }
        await replayAcknowledgementPromise;
      }
      if (webRTCSessionRef.current !== webRTCSession) {
        return;
      }
      webRTCSession.enableMicrophone();

      setSessionState(RealtimeVoiceSessionState.CONNECTED);
      sessionStartTimeMsRef.current = Date.now();
    } catch (error) {
      if (activeSessionAttemptRef.current !== attempt) {
        return;
      }
      await stopVoiceSession();
      throw error;
    }
  }, [agentType, chatSessionId, stopVoiceSession, handleDataEvent, handleWebRTCConnectionStateChange]);

  return {
    sessionState,
    isUserSpeaking,
    isAssistantWorking,
    isAssistantSpeaking,
    microphoneLevel: microphoneLevelRef.current,
    startVoiceSession,
    stopVoiceSession,
  };
}
